/**
 * Serveurs ICE supplémentaires (relais TURN Cloudflare) obtenus au moment de créer
 * ou rejoindre une partie. Tout échec = liste vide : on joue en STUN seul.
 */

const SCHEME = /^(stuns?|turns?):/;
/** Port 53 : bloqué par Chrome, fait patienter Firefox (recommandation Cloudflare). */
const PORT_53 = /:53(\?|$)/;

type Fetch = (url: string, init?: RequestInit) => Promise<Response>;

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

function parseServer(v: unknown): RTCIceServer | null {
  if (!isRecord(v)) return null;
  const raw = Array.isArray(v['urls']) ? v['urls'] : [v['urls']];
  const urls = raw.filter(
    (u): u is string => typeof u === 'string' && SCHEME.test(u) && !PORT_53.test(u),
  );
  if (urls.length === 0) return null;
  const isTurn = urls.some((u) => u.startsWith('turn'));
  const { username, credential } = v;
  if (!isTurn) return { urls };
  if (typeof username !== 'string' || typeof credential !== 'string') return null;
  return { urls, username, credential };
}

/** Réponse de l'API (ou de `/api/ice`) → serveurs ICE valides. */
export function parseIceServers(json: unknown): RTCIceServer[] {
  if (!isRecord(json)) return [];
  const list: unknown[] = Array.isArray(json['iceServers'])
    ? json['iceServers']
    : [json['iceServers']];
  return list.map(parseServer).filter((s): s is RTCIceServer => s !== null);
}

export function mergeIceServers(
  base: readonly RTCIceServer[],
  extra: readonly RTCIceServer[],
): RTCIceServer[] {
  return [...base, ...extra];
}

export async function fetchTurnServers({
  fetchFn,
  url,
  timeoutMs,
}: {
  fetchFn: Fetch;
  url: string;
  timeoutMs: number;
}): Promise<RTCIceServer[]> {
  const abort = new AbortController();
  const timer = setTimeout(() => {
    abort.abort();
  }, timeoutMs);
  try {
    const res = await fetchFn(url, { cache: 'no-store', signal: abort.signal });
    if (!res.ok) return [];
    return parseIceServers(await res.json());
  } catch {
    return [];
  } finally {
    clearTimeout(timer);
  }
}
